import React, { useEffect, useRef, useState } from 'react';

import {
    ArrowRight,
    Brain,
    Download,
    FileDown,
    Lightbulb,
    Trophy,
    Volume2,
    VolumeX,
    X,
} from 'lucide-react';

import { IMSARMBTSReport } from '../../../../types/services.interfaces/mbts-ux';

interface ReportNavbarProps {
    report: IMSARMBTSReport;
    isConsentLocked: boolean;
    isSpotlightActive: boolean;
    onSpotlightChange: (active: boolean) => void;
};

export const ReportNavbar = (props: ReportNavbarProps) => {
    const { report, isConsentLocked, isSpotlightActive, onSpotlightChange } = props;

    const [isAmbientPlaying, setIsAmbientPlaying] = useState(false);
    const [isExportOpen, setIsExportOpen] = useState(false);

    const audioCtxRef = useRef<AudioContext | null>(null);
    const oscillatorRef = useRef<OscillatorNode | null>(null);
    const headerRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
        return () => {
            try {
                oscillatorRef.current?.stop();
            } catch {
                // already stopped
            }
            oscillatorRef.current = null;
            audioCtxRef.current?.close();
            audioCtxRef.current = null;
        };
    }, []);

    // Hide the fixed app navbar only after the report bar reaches the top of the viewport.
    // (On load the report bar already sits within ~72px of the top, so a 72px threshold
    // would hide the app navbar immediately.)
    useEffect(() => {
        const STUCK_CLASS = 'mbts_arv2_nav_stuck';

        const updateStuck = () => {
            const header = headerRef.current;
            if (!header) return;
            const stuck = window.scrollY > 0 && header.getBoundingClientRect().top <= 1;
            document.body.classList.toggle(STUCK_CLASS, stuck);
        };

        updateStuck();
        window.addEventListener('scroll', updateStuck, { passive: true });
        window.addEventListener('resize', updateStuck);
        return () => {
            window.removeEventListener('scroll', updateStuck);
            window.removeEventListener('resize', updateStuck);
            document.body.classList.remove(STUCK_CLASS);
        };
    }, []);

    const toggleAmbientSound = async () => {
        if (isAmbientPlaying) {
            try {
                oscillatorRef.current?.stop();
            } catch {
                // already stopped
            }
            oscillatorRef.current = null;
            await audioCtxRef.current?.close();
            audioCtxRef.current = null;
            setIsAmbientPlaying(false);
            return;
        }

        try {
            const AudioContextCtor = window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
            if (!AudioContextCtor) return;

            const ctx = new AudioContextCtor();
            const oscillator = ctx.createOscillator();
            const gainNode = ctx.createGain();

            oscillator.type = 'sine';
            oscillator.frequency.setValueAtTime(108, ctx.currentTime);
            gainNode.gain.setValueAtTime(0.015, ctx.currentTime);

            oscillator.connect(gainNode);
            gainNode.connect(ctx.destination);
            oscillator.start();

            audioCtxRef.current = ctx;
            oscillatorRef.current = oscillator;
            setIsAmbientPlaying(true);
        } catch {
            setIsAmbientPlaying(false);
        }
    };

    const handleExport = () => {
        setIsExportOpen(false);
        window.print();
    };

    return (
        <>
            <header ref={headerRef} className="mbts_analysis_v2__navbar">
                <div className="mbts_analysis_v2__navbar__inner">
                    <div className="mbts_analysis_v2__navbar__brand">
                        <div className="mbts_analysis_v2__navbar__logo">
                            <Brain />
                        </div>
                        <div className="mbts_analysis_v2__navbar__brand_copy">
                            <div className="mbts_analysis_v2__navbar__brand_row">
                                <span className="mbts_analysis_v2__navbar__brand_name">MindShield™</span>
                                <span className="mbts_analysis_v2__navbar__badge">Cognitive Lab</span>
                            </div>
                            <p className="mbts_analysis_v2__navbar__debrief">
                                Debrief:{' '}
                                <span>{report.scenario_title}</span>
                            </p>
                        </div>
                    </div>

                    <div className="mbts_analysis_v2__navbar__actions">
                        <div className="mbts_analysis_v2__navbar__role">
                            <div className="mbts_analysis_v2__navbar__role__current">
                                <span className="mbts_analysis_v2__navbar__role__dot" />
                                <span className="mbts_analysis_v2__navbar__role__label">Role:</span>
                                <span className="mbts_analysis_v2__navbar__role__value">
                                    {report.player_current_role} ({report.player_current_level})
                                </span>
                            </div>
                            <ArrowRight />
                            <div className="mbts_analysis_v2__navbar__role__target">
                                <Trophy />
                                <span>
                                    Target: {report.player_milestone_role} ({report.player_milestone_level})
                                </span>
                            </div>
                        </div>

                        <button
                            type="button"
                            className={`mbts_analysis_v2__navbar__ctrl${isAmbientPlaying ? ' mbts_analysis_v2__navbar__ctrl--active' : ''}`}
                            onClick={toggleAmbientSound}
                            title="Toggle Focus Audio Soundscape"
                        >
                            {isAmbientPlaying ? <Volume2 /> : <VolumeX />}
                            <span className="mbts_analysis_v2__navbar__ctrl__label">
                                {isAmbientPlaying ? 'Playing Hum' : 'Focus Music'}
                            </span>
                            {isAmbientPlaying && (
                                <span className="mbts_analysis_v2__navbar__waves" aria-hidden>
                                    <span />
                                    <span />
                                    <span />
                                </span>
                            )}
                        </button>

                        <button
                            type="button"
                            className={`mbts_analysis_v2__navbar__ctrl mbts_analysis_v2__navbar__ctrl--spotlight${isSpotlightActive ? ' mbts_analysis_v2__navbar__ctrl--active' : ''}`}
                            onClick={() => onSpotlightChange(!isSpotlightActive)}
                            title="Toggle Spotlight Dimmer for focused reading"
                        >
                            <Lightbulb />
                            <span className="mbts_analysis_v2__navbar__ctrl__label">Spotlight</span>
                        </button>

                        <button
                            type="button"
                            className="mbts_analysis_v2__navbar__export"
                            onClick={() => setIsExportOpen(true)}
                            disabled={isConsentLocked}
                            title="Download PDF Report"
                        >
                            <Download />
                            <span>PDF Report</span>
                        </button>
                    </div>
                </div>
            </header>

            {isExportOpen && (
                <div className="mbts_analysis_v2__export_modal" role="dialog" aria-modal="true">
                    <div
                        className="mbts_analysis_v2__export_modal__backdrop"
                        onClick={() => setIsExportOpen(false)}
                    />
                    <div className="mbts_analysis_v2__export_modal__panel">
                        <button
                            type="button"
                            className="mbts_analysis_v2__export_modal__close"
                            onClick={() => setIsExportOpen(false)}
                            aria-label="Close export dialog"
                        >
                            <X />
                        </button>
                        <div className="mbts_analysis_v2__export_modal__icon">
                            <FileDown />
                        </div>
                        <h3>Generating Executive Summary PDF</h3>
                        <p>
                            Compiling charts, turn-by-turn friction logs, and personalized leadership
                            recommendations into an executive format.
                        </p>
                        <div className="mbts_analysis_v2__export_modal__progress">
                            <div />
                        </div>
                        <div className="mbts_analysis_v2__export_modal__actions">
                            <button type="button" onClick={() => setIsExportOpen(false)}>
                                Cancel
                            </button>
                            <button type="button" onClick={handleExport}>
                                Download PDF
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
